import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGymConfig } from '../context/GymConfigContext';
import Button from './Button';
import NotificationButton from './NotificationButton/NotificationButton';
import '../styles/components.css';

const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { config } = useGymConfig();
  const logoSrc = config?.logo ? `${API_URL}${config.logo}` : null;
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);

  // Cerrar menú al cambiar de ruta
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const close = () => setMenuOpen(false);

  if (!user) return null;

  const dashboardPath =
    user.rol === 'admin' ? '/admin/dashboard' :
    user.rol === 'entrenador' ? '/entrenador/dashboard' :
    '/cliente/dashboard';

  return (
    <nav className="navbar" ref={navRef}>
      <div className="navbar-container">
        <Link to={dashboardPath} className="navbar-brand" onClick={close}>
          {logoSrc
            ? <img src={logoSrc} alt={config.nombre} className="navbar-logo" />
            : (config?.nombre || 'PichTraining')
          }
        </Link>

        {/* Desktop: menú inline | Mobile: oculto hasta que se abra */}
        <div className={`navbar-menu${menuOpen ? ' navbar-menu--open' : ''}`}>
          {user.rol === 'admin' && (
            <>
              <Link to="/admin/dashboard" className="navbar-link" onClick={close}>📊 Dashboard</Link>
              <span className="navbar-divider">|</span>
              <Link to="/admin/usuarios" className="navbar-link" onClick={close}>👥 Usuarios</Link>
              <Link to="/admin/suscripciones" className="navbar-link" onClick={close}>📋 Suscripciones</Link>
              <Link to="/admin/planificaciones" className="navbar-link" onClick={close}>💪 Planificaciones</Link>
              <Link to="/admin/rutinas" className="navbar-link" onClick={close}>📋 Rutinas</Link>
              <Link to="/admin/calendario" className="navbar-link" onClick={close}>📅 Calendario</Link>
              <span className="navbar-divider">|</span>
              <Link to="/admin/generar-horarios" className="navbar-link" onClick={close}>⏰ Bloques</Link>
              <Link to="/admin/asignacion-automatica" className="navbar-link" onClick={close}>🤖 Asignación Auto</Link>
              <Link to="/admin/planes" className="navbar-link" onClick={close}>🎯 Planes</Link>
              <Link to="/admin/ejercicios" className="navbar-link" onClick={close}>🏋️ Ejercicios</Link>
              <span className="navbar-divider">|</span>
              <Link to="/admin/pagos" className="navbar-link" onClick={close}>💰 Pagos</Link>
              <span className="navbar-divider">|</span>
              <Link to="/admin/vista-clase" className="navbar-link" onClick={close}>📺 Vista Clase</Link>
              <span className="navbar-divider">|</span>
              <Link to="/admin/configuracion" className="navbar-link" onClick={close}>⚙️ Configuración</Link>
            </>
          )}

          {user.rol === 'entrenador' && (
            <>
              <Link to="/entrenador/dashboard" className="navbar-link" onClick={close}>📊 Dashboard</Link>
              <span className="navbar-divider">|</span>
              <Link to="/entrenador/clientes" className="navbar-link" onClick={close}>👥 Clientes</Link>
              <Link to="/entrenador/planificaciones" className="navbar-link" onClick={close}>💪 Planificaciones</Link>
              <Link to="/entrenador/rutinas" className="navbar-link" onClick={close}>📋 Rutinas</Link>
            </>
          )}

          {user.rol === 'cliente' && (
            <>
              <Link to="/cliente/dashboard" className="navbar-link" onClick={close}>📊 Dashboard</Link>
              <span className="navbar-divider">|</span>
              <Link to="/cliente/horarios" className="navbar-link" onClick={close}>📅 Reservar</Link>
              <Link to="/cliente/sesiones" className="navbar-link" onClick={close}>💪 Mis Sesiones</Link>
              <Link to="/cliente/planificaciones" className="navbar-link" onClick={close}>📝 Planificaciones</Link>
              <Link to="/cliente/rutinas" className="navbar-link" onClick={close}>📋 Mis Rutinas</Link>
              <Link to="/cliente/suscripcion" className="navbar-link" onClick={close}>💳 Mi Suscripción</Link>
              <Link to="/cliente/pagos" className="navbar-link" onClick={close}>💰 Mis Pagos</Link>
              <Link to="/cliente/perfil" className="navbar-link" onClick={close}>👤 Mi Perfil</Link>
            </>
          )}

          {/* En móvil, logout va dentro del menú */}
          <div className="navbar-menu-footer">
            <Button variant="secondary" size="sm" onClick={handleLogout}>
              Salir
            </Button>
          </div>
        </div>

        {/* Derecha: notificaciones + botón salir en desktop + hamburguesa en móvil */}
        <div className="navbar-right">
          <NotificationButton />
          <div className="navbar-user">
            <Button variant="secondary" size="sm" onClick={handleLogout}>
              Salir
            </Button>
          </div>

          <button
            className="navbar-hamburger"
            onClick={() => setMenuOpen(prev => !prev)}
            aria-label="Abrir menú"
            aria-expanded={menuOpen}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
